import {getDirForMediaSizeType, MediaItemSizeType, repoURL} from '@Libraries/s3-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {decompressFrames, parseGIF} from 'gifuct-js';
import {getWriteBucket} from '@Utils/s3.util';
import {getCompatibleImageFileExtension, UPLOADING_IMAGE_STATUS_TIMEOUT, UserImageUploadError} from '@Utils/image.util';
import {getMaxAllowedUploadFileSize, getReadableMaxAllowedUploadFileSize} from '@Libraries/user-media-library';

import {UserImageSource} from '@Libraries/user-image-source.constants';
import type {UserImageSourceType} from '@Libraries/user-image-source.constants';

export {UserImageSource};
export type {UserImageSourceType};

const REMOVED_BACKGROUND_IMAGE_EXTENSION = 'webp';

export const MAX_USER_IMAGE_DIMENSION = 10800;

export interface UserImageVOResponse {
  id: string;
  hasTransparency: boolean;
  uploaderName: string;
  uploaderId: string;
  ext: string;
  filename: string;
  source: string;
  src: UserImageSourceType;
  width: number;
  height: number;
  isUpscaled?: boolean;
  brandKitName?: string;
  brandKitColors?: string[];
}

const BASE_DIR = 'uploads';

export const getDirPathForUserImage = (sizeType: MediaItemSizeType): string => {
  const subDirForSize = getDirForMediaSizeType(sizeType);
  return `${BASE_DIR}${subDirForSize ? `/${subDirForSize}` : ''}`;
};

export const getUserImageScreenFilename = (hashedFilename: string, extension: string): string => {
  return `${hashedFilename}.${extension}`;
};

export const getRemovedImageBackgroundScreenURL = (imageHashedFilename: string): string => {
  return repoURL(`${getDirPathForUserImage(MediaItemSizeType.SCREEN)}/${getRemovedImageBackgroundFilename(imageHashedFilename)}`, getWriteBucket());
};

export const getRemovedImageBackgroundHighresURL = (imageHashedFilename: string): string => {
  return repoURL(`${getDirPathForUserImage(MediaItemSizeType.HIGH_RES)}/${getRemovedImageBackgroundFilename(imageHashedFilename)}`, getWriteBucket());
};

const getRemovedImageBackgroundFilename = (imageHashedFilename: string): string => {
  return `${imageHashedFilename}_removedbg.${getCompatibleImageFileExtension(REMOVED_BACKGROUND_IMAGE_EXTENSION)}`;
};

export const isMultiFrameGIF = (file: File): Promise<boolean> => {
  const reader = new FileReader();

  return new Promise((resolve) => {
    reader.onload = (event): void => {
      const buffer = event.target?.result as ArrayBuffer;
      const gif = parseGIF(buffer);
      const frames = decompressFrames(gif, true);
      resolve(frames.length > 1);
    };

    reader.onerror = (error): void => {
      console.error('Error reading GIF file:', error);
      resolve(false);
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Performs pre-upload actions, checks the file mime type, gets the total number of files for displaying later
 * and updates the total upload count if an upload is already in progress
 * @return {Array} returns valid files
 * @override
 */
export const filterValidImageFiles = (files: File[], uploadVectors = true, onUploadError?: (file: File) => void): File[] => {
  let invalidFiles = 0;
  const invalidFilesList: File[] = [];
  for (let i = files.length - 1; i >= 0; i--) {
    if (!isImageFileValid(files[i]) || (isVectorFileValid(files[i]) && !uploadVectors)) {
      invalidFiles += 1;
      invalidFilesList.push(files[i]);
      files.splice(i, 1);
    } else if ((isImageFileValid(files[i]) || isVectorFileValid(files[i])) && files[i].size > getMaxAllowedUploadFileSize()) {
      invalidFiles += 1;
      invalidFilesList.push(files[i]);
      files.splice(i, 1);
    }
  }

  if (invalidFiles === 1) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_invalid_image_file', {
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      }),
    });
  } else if (invalidFiles > 1) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_all_invalid_image_files', {
        numfiles: invalidFiles,
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      }),
    });
  }

  if (onUploadError && invalidFilesList.length > 0) {
    for (const badFile of invalidFilesList) {
      onUploadError(badFile);
    }
  }
  return files;
};

export const isImageFileValid = (file: File): boolean => {
  return !!file.type && file.type.includes('image/');
};

export const isVectorFileValid = (file: File): boolean => {
  return file.type === 'image/svg+xml';
};

export const isGifFile = (file: File): boolean => {
  return file.type === 'image/gif';
};

export const getHashedFilenameForStockAsset = (source: UserImageSourceType, stockId: string): string => {
  return `${source}${stockId}`;
};

export const getHashedFileNameFromImageURL = (url: string): string | undefined => {
  const filenameWithExt = url.substring(url.lastIndexOf('/') + 1);
  const extensionIndex = filenameWithExt.lastIndexOf('.');

  if (extensionIndex > 0 && extensionIndex < filenameWithExt.length - 1) {
    const hashedFilename = filenameWithExt.substring(0, extensionIndex);
    const isValidHash = /^[a-f0-9]+$/.test(hashedFilename);
    return isValidHash ? hashedFilename : undefined;
  }

  return undefined;
};

interface AiChatUserImageCreationResponse {
  userImageHashedFilename?: string;
}

export const triggerUserImageCreationFromAiChatImageAsset = async (aiChatImageAssetHashedId: string, proposedUserImageHashedFilename: string): Promise<string> => {
  const response = (await window.PMW.writeLocal('ai/createUserImageForAiChatImageAsset', {
    imageHashedId: aiChatImageAssetHashedId,
    userImageHashedFilename: proposedUserImageHashedFilename,
  })) as AiChatUserImageCreationResponse | undefined;

  return response?.userImageHashedFilename ?? proposedUserImageHashedFilename;
};

export const getUserImageDataWhenProcessed = async (hashedFilename: string): Promise<UserImageVOResponse> => {
  const result = (await window.PMW.pollingAjaxCallAsync((): Promise<string> => Promise.resolve(hashedFilename), checkUserImageStatus, UPLOADING_IMAGE_STATUS_TIMEOUT)) as
    | UserImageVOResponse
    | undefined;

  if (!result) {
    throw new UserImageUploadError(`Timed out waiting for upload processing of image: ${hashedFilename}`);
  }

  return result;
};

const checkUserImageStatus = async (hashedFilename: string): Promise<unknown> => {
  return window.PMW.readLocal('posterimage/getUserImageStatus', {hashedId: hashedFilename});
};

export const waitForUserImageVariantsReady = async (hashedFilename: string): Promise<void> => {
  await window.PMW.pollingAjaxCallAsync((): Promise<string> => Promise.resolve(hashedFilename), checkUserImageVariantsStatus, UPLOADING_IMAGE_STATUS_TIMEOUT);
};

const checkUserImageVariantsStatus = async (hashedFilename: string): Promise<unknown> => {
  return window.PMW.readLocal('ai/getImageVariantsStatus', {hashedFilename});
};
